import React, { useCallback, useMemo, useRef, useState } from 'react';

import { procurementApi } from 'api/procurement';
import { useItemMastersQuery } from 'api/useQueries/WarehouseQueries';
import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Label } from 'shadcn/components/ui/Label';

import { PurchaseOrderItemEditTable } from '../entityView/PurchaseOrderItemEditTable';
import { PurchaseOrderItemLookup } from '../formComponents/PurchaseOrderItemLookup';
import { useCountriesQuery } from '../queries/AddressesQueries';
import { findWeekAndYearForScheduledDeliveryDate } from '../utils/purchaseOrderDateUtils';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

/**
 * @param {Object} props
 * @param {Array<Object>} props.items
 * @param {(updater: any) => void} props.onItemsChange
 * @param {{current: {inputs?: Object, selects?: Object, multi?: Object}}} [props.itemEditStateRef]
 * @param {Set<string|number>} [props.removedItemIds]
 * @param {(updater: any) => void} [props.setRemovedItemIds]
 * @param {string|number|null} [props.vendorId]
 * @param {string} [props.scheduledDeliveryDate]
 * @param {boolean} [props.showValidation]
 * @param {Array<{id: string, sku?: string, messages: string[]}>} [props.itemErrors]
 * @param {string[]} [props.itemSummaryErrors]
 * @param {() => void} [props.onItemEditsChange]
 * @param {(params: { itemMaster: Object, nextRow: Object }) => void} [props.onItemAdded]
 */
export function PurchaseOrderCreateItemsCard({
    itemEditStateRef: itemEditStateRefProp,
    itemErrors = [],
    items,
    itemSummaryErrors = [],
    onItemAdded,
    onItemEditsChange,
    onItemsChange,
    removedItemIds: removedItemIdsProp,
    scheduledDeliveryDate = '',
    setRemovedItemIds: setRemovedItemIdsProp,
    showValidation = false,
    vendorId = null,
}) {
    const [selectedExternalSku, setSelectedExternalSku] = useState('');
    const [selectedItemMasterSku, setSelectedItemMasterSku] = useState('');
    const [isAddingItem, setIsAddingItem] = useState(false);

    const internalItemEditStateRef = useRef({ inputs: {}, selects: {}, multi: {} });
    const [internalRemovedItemIds, setInternalRemovedItemIds] = useState(new Set());
    const nextIdRef = useRef(1);

    const itemEditStateRef = itemEditStateRefProp ?? internalItemEditStateRef;
    const removedItemIds = removedItemIdsProp ?? internalRemovedItemIds;
    const setRemovedItemIds = setRemovedItemIdsProp ?? setInternalRemovedItemIds;

    const { data: countries = [] } = useCountriesQuery({ enabled: true });
    const { data: itemMasters = [] } = useItemMastersQuery({ activeOnly: true, enabled: true, mmOnly: true });

    const itemMastersBySku = useMemo(() => {
        const map = new Map();
        for (const row of itemMasters) {
            map.set(String(row.sku), row);
        }
        return map;
    }, [itemMasters]);

    const canAddItem = Boolean(selectedItemMasterSku);
    const canClearSelection = Boolean(selectedExternalSku || selectedItemMasterSku);

    const handleAddItem = useCallback(async () => {
        if (isAddingItem) return;
        if (!selectedItemMasterSku) return;
        const match = itemMastersBySku.get(String(selectedItemMasterSku));
        if (!match) return;

        setIsAddingItem(true);
        let vendorSku = '';
        try {
            if (vendorId) {
                const resp = await procurementApi.getLastPOItemFromVendor(vendorId, match.sku);
                const last = resp?.status && resp?.data?.rows?.length ? resp.data.rows[0] : null;
                vendorSku = last?.vendorSku ?? '';
            }
        } catch (e) {
            // best-effort: ignore lookup failures and leave vendorSku blank
        } finally {
            setIsAddingItem(false);
        }

        const id = `new-${Date.now()}-${nextIdRef.current}`;
        nextIdRef.current += 1;
        const l1TaxonomyId = match.l1TaxonomyID;
        const weekYear = scheduledDeliveryDate ? findWeekAndYearForScheduledDeliveryDate(scheduledDeliveryDate) : null;
        const nextRow = {
            id,
            sku: String(match.sku),
            itemName: String(match.itemNameInternal ?? match.sku),
            expirationType: match.expirationType ?? '',
            baseUnitsPerCase: match.baseUnitsPerCase ?? '',
            // keep other editable fields empty for now
            expectedExpirationDate: '',
            expectedProductionDate: '',
            qty: '',
            vendorSku,
            csPallet: match.csPallet != null ? String(match.csPallet) : '',
            unitPrice: '',
            week: weekYear?.week ? String(weekYear.week) : '',
            year: weekYear?.year ? String(weekYear.year) : '',
            l1TaxonomyID: l1TaxonomyId,
            purchaseType: '',
            suggestedRetailPrice: '',
            unpurchasedOutcome: '',
            sourcingReasons: [],
            countryOfOriginId: '',
        };

        onItemsChange((prev) => {
            const base = Array.isArray(prev) ? prev : [];
            return [...base, nextRow];
        });

        if (typeof onItemAdded === 'function') {
            onItemAdded({ itemMaster: match, nextRow });
        }

        // Reset lookup UI for next add.
        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
    }, [
        isAddingItem,
        itemMastersBySku,
        onItemAdded,
        onItemsChange,
        scheduledDeliveryDate,
        selectedItemMasterSku,
        vendorId,
    ]);

    const handleEditsChange = useCallback(() => {
        if (typeof onItemEditsChange === 'function') {
            onItemEditsChange();
        }
        // Stage 1b: table edits are stored in itemEditStateRef (uncontrolled) only.
        // Wiring the edits back into `items` is part of Stage 2.
    }, [onItemEditsChange]);

    const handleRemoveRow = useCallback(
        (rowId) => {
            onItemsChange((prev) =>
                Array.isArray(prev) ? prev.filter((row) => String(row?.id) !== String(rowId)) : [],
            );

            // Clean up any tracked edit state for this row (best-effort)
            if (itemEditStateRef?.current?.inputs) delete itemEditStateRef.current.inputs[rowId];
            if (itemEditStateRef?.current?.selects) delete itemEditStateRef.current.selects[rowId];
            if (itemEditStateRef?.current?.multi) delete itemEditStateRef.current.multi[rowId];
        },
        [itemEditStateRef, onItemsChange],
    );

    const handleClearSelections = useCallback(() => {
        setSelectedExternalSku('');
        setSelectedItemMasterSku('');
    }, []);

    const showErrors = showValidation === true;
    const itemErrorMessages = useMemo(
        () => [
            ...itemSummaryErrors.map((message) => ({ key: `summary-${message}`, message })),
            ...itemErrors.flatMap((error) =>
                error.messages.map((message) => ({
                    key: `item-${error.id}-${message}`,
                    message,
                })),
            ),
        ],
        [itemErrors, itemSummaryErrors],
    );

    return (
        <Card>
            <CardHeader className="pb-0">
                <CardTitle>Items in Purchase Order</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
                <div className="flex flex-col gap-6">
                    {showErrors && itemErrorMessages.length > 0 ? (
                        <Alert variant="destructive">
                            <AlertTitle>Fix the following item issues to save</AlertTitle>
                            <AlertDescription>
                                <ul className="list-disc pl-5">
                                    {itemErrorMessages.map(({ key, message }) => (
                                        <li key={key}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <Label className={labelClassName}>
                                Look up an Item via an External Sku or Item Master Sku
                            </Label>
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div className="md:col-span-2">
                                <PurchaseOrderItemLookup
                                    externalSku={selectedExternalSku}
                                    itemMasterSku={selectedItemMasterSku}
                                    onExternalSkuChange={setSelectedExternalSku}
                                    onItemMasterSkuChange={setSelectedItemMasterSku}
                                />
                            </div>
                            <div className="flex flex-col justify-end">
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleClearSelections}
                                        disabled={!canClearSelection}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={handleAddItem}
                                        disabled={!canAddItem || isAddingItem}
                                    >
                                        Add Item
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {Array.isArray(items) && items.length > 0 ? (
                        <div className="overflow-x-auto">
                            <PurchaseOrderItemEditTable
                                rows={items}
                                countries={countries}
                                itemEditStateRef={itemEditStateRef}
                                removedItemIds={removedItemIds}
                                setRemovedItemIds={setRemovedItemIds}
                                onItemEditsChange={handleEditsChange}
                                onRemoveNewRow={handleRemoveRow}
                            />
                        </div>
                    ) : (
                        <div className="flex min-h-[160px] items-center justify-center rounded-md border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                            No items added yet. Add an item to proceed.
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

PurchaseOrderCreateItemsCard.displayName = 'PurchaseOrderCreateItemsCard';
